"use client";

import { useState } from "react";
import Link from "next/link";
import Logo from "../components/Logo";

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setSubmitted(false);
    setError(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          subject: subject.trim() || undefined,
          message: message.trim(),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(data.error ?? "Something went wrong. Please try again.");
        return;
      }
      setSubmitted(true);
      setName("");
      setEmail("");
      setPhone("");
      setSubject("");
      setMessage("");
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-12">
        <div className="mb-6 flex items-center justify-between">
          <Logo href="/" />
          <Link
            href="/"
            className="text-sm font-medium text-amber-300 hover:text-amber-200"
          >
            ← Back to home
          </Link>
        </div>
        <h1 className="mt-6 font-heading text-2xl font-semibold text-slate-50 sm:text-3xl">
          Contact us
        </h1>
        <p className="mt-2 text-sm text-slate-400">
          Have a question about your assessment, report, or billing? Send us a note—we read every
          message.
        </p>

        <div className="mt-10 grid gap-10 sm:grid-cols-1 lg:grid-cols-2">
          <div className="space-y-6">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-100">
                Get in touch
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                Submissions are delivered to our team directly (no automated email is sent from this
                form). For urgent billing issues, include your account email.
              </p>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Email
              </h3>
              <a
                href="mailto:support@aptinomic.com"
                className="mt-1 block text-sm text-amber-300 hover:text-amber-200"
              >
                support@aptinomic.com
              </a>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Product
              </h3>
              <p className="mt-1 text-sm text-slate-300">
                Aptinomic is premium AI career guidance: a five-layer assessment, structured career
                dialogue, and a personalised report (from ₹999) you generate when you are ready from
                your dashboard.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-xl">
            {submitted ? (
              <div className="rounded-xl bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
                Thank you. We have received your message and will follow up if needed.
              </div>
            ) : (
              <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
                {error ? (
                  <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                    {error}
                  </div>
                ) : null}
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-medium text-slate-400">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    minLength={2}
                    maxLength={120}
                    className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-medium text-slate-400">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-xs font-medium text-slate-400">
                    Phone <span className="text-slate-600">(optional)</span>
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={40}
                    className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    placeholder="+91 …"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-xs font-medium text-slate-400">
                    Subject <span className="text-slate-600">(optional)</span>
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={200}
                    className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    placeholder="e.g. Report access, billing, feedback"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs font-medium text-slate-400">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    minLength={10}
                    maxLength={8000}
                    className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    placeholder="How can we help?"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full rounded-xl bg-amber-500 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-amber-400 disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </div>

        <p className="mt-12 text-center text-xs text-slate-500">
          <Link href="/" className="hover:text-slate-400">Aptinomic</Link>
          {" · "}
          <Link href="/terms" className="hover:text-slate-400">Terms of Use</Link>
          {" · "}
          <Link href="/privacy" className="hover:text-slate-400">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
